import { useState, Dispatch, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StateInterface from 'Client/redux/types';
import useGa from 'Client/hooks/useGa';
import { UNBLOCK_USER_OVERLAY_EVENTS } from 'Common/ga/types';
import useRouter from 'AppShell/hooks/useRouter';
import { unblockUserActions } from 'Client/redux/user/unblock/actions';

interface UseUnblockOverlay {
    rejectBlockedUserCount: number;
    setRejectBlockedUserCount: Dispatch<SetStateAction<number>>;
    shouldShowBlockedUserOverlay: boolean;
    isFetchingUnblock: boolean;
    trackUnblockOverlayAccept: () => void;
    trackUnblockOverlayRejectFirstTime: () => void;
    trackUnblockOverlayRejectSecondTime: () => void;
    trackUnblockOverlayDismiss: () => void;
    trackUnblockOverlayImpression: () => void;
    dismissUnblockOverlay: (showingBlockedUsersOverlay: boolean) => void;
}

export default function useUnblockOverlay(): UseUnblockOverlay {
    const dispatch = useDispatch();
    const gaTracking = useGa();
    const {
        location: { pathname, search },
    } = useRouter();
    const isFetchingUnblock = useSelector<StateInterface, boolean>(state => state.unblock.isFetching);
    const shouldShowDataPolicyOverlay = useSelector<StateInterface, boolean>(
        state => state.appShellLogin.shouldShowDataPolicyOverlay
    );

    const [rejectBlockedUserCount, setRejectBlockedUserCount] = useState(0);

    const shouldShowBlockedUserOverlay = shouldShowDataPolicyOverlay && rejectBlockedUserCount < 2;

    const trackUnblockOverlayImpression = () => {
        gaTracking.trackGenericEvent(UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CATEGORY, {
            action: UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_IMPRESSION,
            label: `${pathname}${search}`,
        });
    };

    const trackUnblockOverlayAccept = () => {
        gaTracking.trackGenericEvent(UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CATEGORY, {
            action: UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CLICK,
            label: `Accept`,
        });
    };

    const trackUnblockOverlayRejectFirstTime = () => {
        gaTracking.trackGenericEvent(UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CATEGORY, {
            action: UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CLICK,
            label: `Reject first time`,
        });
    };

    const trackUnblockOverlayRejectSecondTime = () => {
        gaTracking.trackGenericEvent(UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CATEGORY, {
            action: UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CLICK,
            label: `Reject second time`,
        });
    };

    const trackUnblockOverlayDismiss = () => {
        gaTracking.trackGenericEvent(UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CATEGORY, {
            action: UNBLOCK_USER_OVERLAY_EVENTS.UNBLOCK_OVERLAY_CLICK,
            label: 'Dismiss',
        });
    };

    const dismissUnblockOverlay = (showingBlockedUsersOverlay: boolean) => {
        setRejectBlockedUserCount(0);
        dispatch(unblockUserActions.success());
        if (showingBlockedUsersOverlay) {
            trackUnblockOverlayDismiss();
        }
    };

    return {
        rejectBlockedUserCount,
        setRejectBlockedUserCount,
        shouldShowBlockedUserOverlay,
        isFetchingUnblock,
        trackUnblockOverlayImpression,
        trackUnblockOverlayAccept,
        trackUnblockOverlayRejectFirstTime,
        trackUnblockOverlayRejectSecondTime,
        trackUnblockOverlayDismiss,
        dismissUnblockOverlay,
    };
}
